<template>
  <div class="content-wrapper">
    <div class="content-body">

      <!-- ACCESS GUARD -->
      <div
        v-if="!$can('CHANTIER:admin')"
        class="chantiers-admin-empty chantiers-admin-empty--error"
      >
        <feather-icon
          icon="LockIcon"
          size="32"
        />
        <p class="mt-1 mb-0">
          Vous n'avez pas les droits pour accéder à cette page.
        </p>
      </div>

      <template v-else>
        <!-- TOOLBAR -->
        <div class="chantiers-admin-toolbar">
          <div class="chantiers-admin-toolbar__title">
            <feather-icon
              icon="ArchiveIcon"
              size="18"
              class="mr-50"
            />
            <strong>Chantiers archivés</strong>
          </div>
          <b-form-input
            v-model="search"
            placeholder="Rechercher un chantier…"
            class="chantiers-admin-search"
            @input="onSearchInput"
          />
          <b-form-select
            v-model="filtreProcessus"
            :options="processusOptions"
            class="chantiers-admin-search"
            @change="onProcessusChange"
          />
        </div>

        <!-- LOADING -->
        <div
          v-if="loading"
          class="chantiers-admin-empty"
        >
          <b-spinner class="mb-1" />
          <p class="mt-1 mb-0">
            Chargement des chantiers archivés…
          </p>
        </div>

        <!-- ERROR -->
        <div
          v-else-if="error"
          class="chantiers-admin-empty chantiers-admin-empty--error"
        >
          <feather-icon
            icon="AlertTriangleIcon"
            size="32"
          />
          <p class="mt-1 mb-1">
            {{ error }}
          </p>
          <b-button
            variant="outline-primary"
            size="sm"
            @click="load"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="14"
              class="mr-50"
            />
            Réessayer
          </b-button>
        </div>

        <!-- EMPTY -->
        <div
          v-else-if="items.length === 0"
          class="chantiers-admin-empty"
        >
          <feather-icon
            icon="InboxIcon"
            size="32"
          />
          <p class="mt-1 mb-0">
            Aucun chantier archivé.
          </p>
        </div>

        <!-- TABLE -->
        <div
          v-else
          class="chantiers-admin-card"
        >
          <b-table
            :items="items"
            :fields="fields"
            responsive
            hover
            class="mb-0"
          >
            <template #cell(nom)="data">
              <b-link
                class="chantiers-admin-link"
                @click="goToDetail(data.item)"
              >
                {{ data.item.nom }}
              </b-link>
              <div
                v-if="data.item.sousCategorie"
                class="chantiers-admin-sub"
              >
                {{ data.item.sousCategorie }}
              </div>
            </template>
            <template #cell(categorieLibelle)="data">
              <b-badge
                :style="{ backgroundColor: data.item.categorieCouleur || '#1c4d4d' }"
              >
                {{ data.item.categorieLibelle }}
              </b-badge>
            </template>
            <template #cell(statut)="data">
              <b-badge :style="{ backgroundColor: statutCouleur(data.item.statut) }">
                {{ statutLibelle(data.item.statut) }}
              </b-badge>
            </template>
            <template #cell(archivedAt)="data">
              {{ formatDate(data.item.archivedAt) }}
            </template>
            <template #cell(actions)="data">
              <b-button
                variant="flat-secondary"
                size="sm"
                title="Ouvrir"
                @click="goToDetail(data.item)"
              >
                <feather-icon
                  icon="EyeIcon"
                  size="14"
                />
              </b-button>
              <b-button
                variant="flat-primary"
                size="sm"
                title="Désarchiver"
                @click="confirmUnarchive(data.item)"
              >
                <feather-icon
                  icon="RotateCcwIcon"
                  size="14"
                />
              </b-button>
            </template>
          </b-table>
        </div>

        <!-- PAGINATION -->
        <div
          v-if="!loading && !error && totalCount > pageSize"
          class="d-flex justify-content-center mt-2"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="pageSize"
            @input="load"
          />
        </div>
      </template>

    </div>
  </div>
</template>

<script>
import {
  BButton,
  BBadge,
  BFormInput,
  BFormSelect,
  BLink,
  BPagination,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import { successAlert, errorAlert } from '@/libs/sweetAlerts/alerts'

export default {
  name: 'ArchivedChantierAdmin',
  components: {
    BButton,
    BBadge,
    BFormInput,
    BFormSelect,
    BLink,
    BPagination,
    BSpinner,
    BTable,
  },
  data() {
    return {
      items: [],
      statuts: [],
      processus: [],
      filtreProcessus: null,
      totalCount: 0,
      currentPage: 1,
      pageSize: 12,
      search: '',
      searchTimer: null,
      loading: true,
      error: null,
      fields: [
        { key: 'nom', label: 'Chantier' },
        { key: 'categorieLibelle', label: 'Catégorie' },
        { key: 'statut', label: 'Statut' },
        { key: 'archivedAt', label: 'Archivé le' },
        { key: 'actions', label: 'Actions', class: 'text-right' },
      ],
    }
  },
  computed: {
    processusOptions() {
      return [
        { value: null, text: 'Tous les processus' },
        ...this.processus.map(p => ({ value: p.id, text: p.libelle })),
      ]
    },
  },
  created() {
    if (this.$can('CHANTIER:admin')) {
      this.loadStatuts()
      this.loadProcessus()
      this.load()
    } else {
      this.loading = false
    }
  },
  methods: {
    async loadStatuts() {
      try {
        this.statuts = await this.$http.myressif.chantiers.getStatuts()
      } catch (e) {
        this.statuts = []
      }
    },
    async loadProcessus() {
      try {
        const response = await this.$http.myressif.chantiers.listProcessus()
        this.processus = (response && response.items) || []
      } catch (e) {
        this.processus = []
      }
    },
    onProcessusChange() {
      this.currentPage = 1
      this.load()
    },
    async load() {
      this.loading = true
      this.error = null
      try {
        const response = await this.$http.myressif.chantiers.searchArchived({
          pageNumber: this.currentPage,
          pageSize: this.pageSize,
          search: this.search || undefined,
          processusId: this.filtreProcessus || undefined,
        })
        this.items = (response && response.items) || []
        this.totalCount = (response && response.totalCount) || 0
      } catch (e) {
        this.items = []
        this.totalCount = 0
        this.error = 'Une erreur est survenue lors du chargement des chantiers archivés.'
      } finally {
        this.loading = false
      }
    },
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1
        this.load()
      }, 300)
    },
    statutLibelle(id) {
      const s = this.statuts.find(x => x.id === id)
      return s ? s.valeur : '—'
    },
    statutCouleur(id) {
      const s = this.statuts.find(x => x.id === id)
      return (s && s.couleur) || '#5A7373'
    },
    formatDate(value) {
      if (!value) return '—'
      const d = new Date(value)
      return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR')
    },
    goToDetail(item) {
      this.$router.push({ name: 'chantier-detail', params: { id: item.id } })
    },
    confirmUnarchive(item) {
      this.$bvModal
        .msgBoxConfirm(`Désarchiver le chantier « ${item.nom} » ? Il réapparaîtra dans la liste active.`, {
          title: 'Confirmation de désarchivage',
          size: 'sm',
          okVariant: 'primary',
          okTitle: 'Désarchiver',
          cancelTitle: 'Annuler',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(value => {
          if (value === true) this.doUnarchive(item)
        })
    },
    async doUnarchive(item) {
      try {
        await this.$http.myressif.chantiers.unarchive(item.id)
        successAlert.fire({
          title: 'Chantier désarchivé',
          text: 'Le chantier a été restauré dans la liste active.',
        })
        // Si on vide la page courante, reculer d'une page le cas échéant.
        if (this.items.length === 1 && this.currentPage > 1) this.currentPage -= 1
        await this.load()
      } catch (e) {
        errorAlert.fire({
          title: 'Oops',
          text: 'Le désarchivage du chantier a échoué.',
        })
      }
    },
  },
}
</script>

<style scoped lang="scss">
$at-green:   #1c4d4d;
$at-orange:  #ff6131;
$at-ink-500: #5A7373;
$at-ink-100: #E4ECEC;

.chantiers-admin-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px;
  background: #fff;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 16px;

  &__title {
    display: inline-flex;
    align-items: center;
    color: $at-green;
    font-size: 16px;
  }
}

.chantiers-admin-search {
  max-width: 280px;
}

.chantiers-admin-card {
  background: #fff;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  overflow: hidden;
}

.chantiers-admin-empty {
  text-align: center;
  padding: 48px 16px;
  color: $at-ink-500;
  background: #fff;
  border: 1px dashed $at-ink-100;
  border-radius: 10px;

  &--error {
    color: $at-orange;
    border-color: rgba(255, 97, 49, .3);
  }
}

.chantiers-admin-link {
  font-weight: 600;
  cursor: pointer;
}

.chantiers-admin-sub {
  font-size: 12px;
  color: $at-ink-500;
}
</style>
